import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, CalendarClock, Store as StoreIcon, MessageSquareWarning } from 'lucide-react';
import api from '../../utils/api';

/**
 * BuyerAppointmentsBlock — Wave 35
 * Mirror of VendorAppointmentsBlock but from the buyer's side.
 * Buyer can cancel, reschedule, or open a "shop no-show" dispute.
 */
export default function BuyerAppointmentsBlock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('upcoming');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/marketplace/my-appointments');
      setRows(r.data.as_buyer || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this appointment?\nNote: Non-refundable per booking terms.')) return;
    try { await api.post(`/marketplace/appointment/${id}/cancel`); toast.success('❌ Cancelled'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reschedule = async (id) => {
    const newDate = window.prompt('New date (YYYY-MM-DD):');
    if (!newDate) return;
    try {
      await api.post(`/marketplace/appointment/${id}/reschedule?new_date=${encodeURIComponent(newDate)}`);
      toast.success('📅 Rescheduled'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reschedule failed'); }
  };
  const reportShopNoShow = async (a) => {
    if (!window.confirm(`Report that ${a.shop_name} did not show up?\nAdmin will review and refund within 24h.`)) return;
    const note = window.prompt('Optional note (max 280 chars):') || '';
    try {
      await api.post(`/marketplace/appointment/${a.id}/shop-no-show`, { note: note.slice(0, 280) });
      toast.success('📩 Dispute opened — admin will review'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filtered = rows.filter((a) => {
    if (filter === 'upcoming') return ['booked', 'rescheduled', 'disputed'].includes(a.status);
    if (filter === 'completed') return a.status === 'completed';
    if (filter === 'cancelled') return ['cancelled_by_buyer', 'cancelled_by_shop', 'no_show'].includes(a.status);
    return true;
  });

  const statusStyle = (st) => {
    switch (st) {
      case 'booked':            return { bg: 'bg-emerald-500/10 border-emerald-400/30', text: '✅ Booked' };
      case 'rescheduled':       return { bg: 'bg-cyan-500/10 border-cyan-400/30',       text: '📅 Rescheduled' };
      case 'completed':         return { bg: 'bg-emerald-500/20 border-emerald-400/50', text: '✅ Completed' };
      case 'cancelled_by_shop': return { bg: 'bg-rose-500/10 border-rose-400/30',       text: '❌ Cancelled by shop (refunded)' };
      case 'cancelled_by_buyer':return { bg: 'bg-slate-500/10 border-slate-400/30',     text: '❌ Cancelled by you' };
      case 'no_show':           return { bg: 'bg-amber-500/10 border-amber-400/30',     text: '😕 No-show (refunded)' };
      case 'disputed':          return { bg: 'bg-purple-500/10 border-purple-400/30',   text: '📩 Dispute open' };
      default:                  return { bg: 'bg-slate-800',                             text: st };
    }
  };

  return (
    <div className="rounded-xl border border-cyan-400/40 bg-slate-900/60 p-3 space-y-2" data-testid="buyer-appointments-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-black text-cyan-300 flex items-center gap-1.5">
          <CalendarClock size={14} /> My Appointments (as buyer)
        </div>
        <div className="flex gap-1 bg-slate-800 rounded-lg p-0.5">
          {['upcoming', 'completed', 'cancelled'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    data-testid={`buyer-apt-filter-${f}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-black capitalize ${filter === f ? 'bg-cyan-500 text-slate-900' : 'text-slate-300'}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-center text-[11px] opacity-60 py-4">⏳ Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-6 opacity-60 text-xs">No {filter} appointments</div>
      )}

      <div className="space-y-1.5">
        {filtered.map((a) => {
          const s = statusStyle(a.status);
          return (
            <div key={a.id} data-testid={`buyer-apt-${a.id.slice(0, 8)}`}
                 className={`p-2 rounded-lg border ${s.bg}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black flex items-center gap-1"><StoreIcon size={11} /> {a.shop_name || a.shop_slug}</div>
                  <div className="text-[10px] opacity-80">
                    📅 {a.date} · {a.mode === 'virtual' ? '💻 Virtual' : '🏬 Physical'} · 💰 {a.amount_paid_at} AT paid
                    {a.amount_remaining_at > 0 && <span> · <span className="text-yellow-300">{a.amount_remaining_at} AT due</span></span>}
                  </div>
                  {a.status === 'disputed' && a.dispute_note && (
                    <div className="text-[10px] opacity-70 italic mt-0.5">Your note: "{a.dispute_note}"</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-slate-900/50">{s.text}</span>
                  {['booked', 'rescheduled'].includes(a.status) && (
                    <div className="flex gap-1 flex-wrap justify-end">
                      <button onClick={() => reschedule(a.id)} data-testid={`buyer-apt-reschedule-${a.id.slice(0,8)}`}
                              title="Change appointment date"
                              className="px-2 py-0.5 rounded bg-cyan-500 text-slate-900 text-[10px] font-black flex items-center gap-0.5 hover:brightness-110">
                        <CalendarClock size={10} /> Reschedule
                      </button>
                      <button onClick={() => reportShopNoShow(a)} data-testid={`buyer-apt-shop-noshow-${a.id.slice(0,8)}`}
                              title="Shop didn't show — open dispute for refund"
                              className="px-2 py-0.5 rounded bg-purple-600 text-white text-[10px] font-black flex items-center gap-0.5 hover:brightness-110">
                        <MessageSquareWarning size={10} /> Shop didn't show
                      </button>
                      <button onClick={() => cancel(a.id)} data-testid={`buyer-apt-cancel-${a.id.slice(0,8)}`}
                              title="Cancel (non-refundable per terms)"
                              className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-0.5">
                        <XCircle size={10} /> Cancel
                      </button>
                    </div>
                  )}
                  {a.status === 'completed' && (
                    <span className="text-[9px] opacity-70 flex items-center gap-0.5"><CheckCircle2 size={10} className="text-emerald-300" /> Settled</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
